// src/components/TripList.jsx
// Shows list of saved trips with load/delete functionality
// 🔹 UPDATED: Uses getAllTripsMeta() for fast loading without memory crashes

import React, { useState, useEffect } from 'react';
import { getAllTripsMeta, getTrip, deleteTrip, getStorageEstimate } from '../utils/tripStorage';
import './TripList.css';

export default function TripList({ onLoadTrip, onClose, onNewTrip }) {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [storageInfo, setStorageInfo] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [loadingTripId, setLoadingTripId] = useState(null); // 🔹 NEW: Track which trip is being loaded

  useEffect(() => {
    loadTrips();
    loadStorageInfo();
  }, []);

  async function loadTrips() {
    setLoading(true);
    try {
      // 🔹 UPDATED: Use fast metadata loading instead of full trip data
      const tripsMeta = await getAllTripsMeta();
      setTrips(tripsMeta);
    } catch (error) {
      console.error('Failed to load trips:', error);
    } finally {
      setLoading(false);
    }
  }

  async function loadStorageInfo() {
    const info = await getStorageEstimate();
    setStorageInfo(info);
  }

  // 🔹 NEW: Load full trip data when user clicks to open
  async function handleLoadTrip(tripMeta) {
    setLoadingTripId(tripMeta.id);
    try {
      const fullTrip = await getTrip(tripMeta.id);
      if (fullTrip) {
        onLoadTrip(fullTrip);
      } else {
        alert('Failed to load trip. It may have been deleted.');
      }
    } catch (error) {
      console.error('Failed to load trip:', error);
      alert('Failed to load trip. Please try again.');
    } finally {
      setLoadingTripId(null);
    }
  }

  async function handleDelete(tripId, tripName) {
    if (!window.confirm(`Delete "${tripName}"? This cannot be undone.`)) {
      return;
    }

    setDeletingId(tripId);
    try {
      const success = await deleteTrip(tripId);
      if (success) {
        setTrips(prev => prev.filter(t => t.id !== tripId));
        await loadStorageInfo(); // Refresh storage stats
      } else {
        alert('Failed to delete trip. Please try again.');
      }
    } catch (error) {
      console.error('Delete failed:', error);
      alert('Failed to delete trip. Please try again.');
    } finally {
      setDeletingId(null);
    }
  }

  function formatDate(timestamp) {
    if (!timestamp) return 'Unknown date';
    const date = new Date(timestamp);
    return date.toLocaleDateString(undefined, { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  // 🔹 UPDATED: Use metadata instead of computing from full trip
  function getTripStats(tripMeta) {
    return { 
      photoCount: tripMeta.photoCount || 0, 
      dayCount: tripMeta.stepCount || 0  // stepCount approximates days
    };
  }

  // 🔹 UPDATED: Use thumbnail from metadata (or null for placeholder)
  function getThumbnail(tripMeta) {
    return tripMeta.thumbnail || null;
  }

  return (
    <div className="trip-list-overlay" onClick={onClose}>
      <div className="trip-list-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="trip-list-header">
          <div>
            <h2 className="trip-list-title">My Trips</h2>
            {storageInfo && (
              <p className="trip-list-storage">
                Storage: {(storageInfo.usage / 1024 / 1024).toFixed(1)} MB used 
                {storageInfo.quota > 0 && ` of ${(storageInfo.quota / 1024 / 1024).toFixed(0)} MB`}
              </p>
            )}
          </div>
          <button className="trip-list-close" onClick={onClose}>✕</button>
        </div>

        {/* Trip List */}
        <div className="trip-list-content">
          {loading ? (
            <div className="trip-list-loading">Loading trips...</div>
          ) : trips.length === 0 ? (
            <div className="trip-list-empty">
              <div className="trip-list-empty-icon">📚</div>
              <h3>No trips yet</h3>
              <p>Create your first trip to get started!</p>
              <button className="btn btn-primary" onClick={onNewTrip}>
                Create new trip
              </button>
            </div>
          ) : (
            <div className="trip-list-grid">
              {trips.map((trip) => {
                const { photoCount, dayCount } = getTripStats(trip);
                const thumbnail = getThumbnail(trip);
                const isDeleting = deletingId === trip.id;
                const isLoadingThis = loadingTripId === trip.id; // 🔹 NEW

                return (
                  <div key={trip.id} className={`trip-card ${isLoadingThis ? 'trip-card-loading' : ''}`}>
                    {/* Thumbnail */}
                    <div 
                      className="trip-card-thumbnail"
                      style={{ 
                        backgroundImage: thumbnail ? `url(${thumbnail})` : 'none',
                        backgroundColor: thumbnail ? 'transparent' : '#334155'
                      }}
                      onClick={() => !isLoadingThis && handleLoadTrip(trip)}
                    >
                      {!thumbnail && (
                        <div className="trip-card-no-photo">📷</div>
                      )}
                      {/* 🔹 NEW: Loading overlay */}
                      {isLoadingThis && (
                        <div className="trip-card-loading-overlay">
                          <div className="trip-card-spinner"></div>
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="trip-card-info" onClick={() => !isLoadingThis && handleLoadTrip(trip)}>
                      <h3 className="trip-card-name">{trip.name || 'Untitled Trip'}</h3>
                      <p className="trip-card-stats">
                        {photoCount} photo{photoCount !== 1 ? 's' : ''}
                        {dayCount > 0 && ` · ${dayCount} step${dayCount !== 1 ? 's' : ''}`}
                      </p>
                      <p className="trip-card-date">
                        {formatDate(trip.updatedAt || trip.savedAt)}
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="trip-card-actions">
                      <button
                        className="trip-card-btn trip-card-btn-delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(trip.id, trip.name);
                        }}
                        disabled={isDeleting || isLoadingThis}
                        title="Delete trip"
                      >
                        {isDeleting ? '⏳' : '🗑️'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {trips.length > 0 && (
          <div className="trip-list-footer">
            <button className="btn btn-primary" onClick={onNewTrip}>
              + New Trip
            </button>
          </div>
        )}
      </div>
    </div>
  );
}